import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';

type Org = { id: string; name: string; country: string | null; timezone: string; default_currency: string; default_locale: string; preventive_window_days: number; default_labour_rate_id: string | null };
type Kpi = { kpi_key: string; target_pct: number };

export function Settings() {
  const { t, i18n } = useTranslation();
  const [org, setOrg] = useState<Org | null>(null);
  const [kpis, setKpis] = useState<Kpi[]>([]);
  const [win, setWin] = useState('');
  const [savedWin, setSavedWin] = useState(false);
  const [rates, setRates] = useState<{ id: string; name: string; hourly_rate: number; currency: string }[]>([]);
  const [newRate, setNewRate] = useState({ name: '', hourly_rate: '' });

  const loadRates = async () => {
    const r = await supabase.from('labour_rates').select('id,name,hourly_rate,currency').is('deleted_at', null).order('name');
    setRates((r.data as any[]) ?? []);
  };

  // ── CRM config: pipeline stages + lead sources (org-configurable) ──
  const [stages, setStages] = useState<{ id: string; name: string; name_i18n?: Record<string,string>|null; position: number; is_won: boolean; is_lost: boolean }[]>([]);
  const [sources, setSources] = useState<{ id: string; name: string; name_i18n?: Record<string,string>|null }[]>([]);
  const [newStage, setNewStage] = useState('');
  const [newSource, setNewSource] = useState('');
  const loadCrm = async () => {
    const [st, sr] = await Promise.all([
      supabase.from('pipeline_stages').select('id,name,name_i18n,position,is_won,is_lost').is('deleted_at', null).order('position'),
      supabase.from('lead_sources').select('id,name,name_i18n').is('deleted_at', null).order('name'),
    ]);
    setStages((st.data as any[]) ?? []);
    setSources((sr.data as any[]) ?? []);
  };
  async function addStage() {
    if (!org || !newStage.trim()) return;
    const pos = stages.length ? Math.max(...stages.map((s) => s.position)) + 1 : 0;
    const { error } = await supabase.from('pipeline_stages').insert({ organization_id: org.id, name: newStage.trim(), name_i18n: { en: newStage.trim() }, position: pos } as any);
    if (!error) { setNewStage(''); loadCrm(); }
  }
  async function addSource() {
    if (!org || !newSource.trim()) return;
    const { error } = await supabase.from('lead_sources').insert({ organization_id: org.id, name: newSource.trim(), name_i18n: { en: newSource.trim() } } as any);
    if (!error) { setNewSource(''); loadCrm(); }
  }

  // ── tax rates (org-configurable; no rate hard-coded) ──
  const [taxes, setTaxes] = useState<{ id: string; name: string; rate_pct: number; is_inclusive: boolean; active: boolean }[]>([]);
  const [newTax, setNewTax] = useState({ name: '', rate_pct: '', is_inclusive: '0' });
  const loadTax = async () => {
    const r = await supabase.from('tax_rates').select('id,name,rate_pct,is_inclusive,active').is('deleted_at', null).order('name');
    setTaxes((r.data as any[]) ?? []);
  };
  async function addTax() {
    if (!org || !newTax.name.trim() || !(Number(newTax.rate_pct) >= 0)) return;
    const { error } = await supabase.from('tax_rates').insert({ organization_id: org.id, name: newTax.name.trim(), rate_pct: Number(newTax.rate_pct), is_inclusive: newTax.is_inclusive === '1' } as any);
    if (!error) { setNewTax({ name: '', rate_pct: '', is_inclusive: '0' }); loadTax(); }
  }
  useEffect(() => {
    (async () => {
      const [o, k] = await Promise.all([
        supabase.from('organizations').select('id,name,country,timezone,default_currency,default_locale,preventive_window_days,default_labour_rate_id').is('deleted_at', null).limit(1).single(),
        supabase.from('organization_kpi_targets').select('kpi_key,target_pct').is('deleted_at', null),
      ]);
      setOrg(o.data as Org);
      setWin(String((o.data as Org)?.preventive_window_days ?? 30));
      setKpis((k.data as Kpi[]) ?? []);
      loadRates();
      loadNotif();
      loadCrm();
      loadTax();
    })();
  }, []);

  async function setDefaultRate(rateId: string) {
    if (!org) return;
    const { error } = await supabase.rpc('set_org_default_labour_rate', { p_org: org.id, p_rate: rateId || null });
    if (!error) setOrg({ ...org, default_labour_rate_id: rateId || null });
  }
  async function addRate() {
    if (!org || !newRate.name.trim() || !(Number(newRate.hourly_rate) >= 0)) return;
    const { error } = await supabase.from('labour_rates').insert({ organization_id: org.id, name: newRate.name.trim(), hourly_rate: Number(newRate.hourly_rate), currency: org.default_currency } as any);
    if (!error) { setNewRate({ name: '', hourly_rate: '' }); loadRates(); }
  }

  // ── notification preferences + recent alerts ──
  const PREF_KEYS = ['email_enabled', 'alert_due_j30', 'alert_due_j7', 'alert_overdue', 'alert_wo_overdue', 'alert_anomaly'] as const;
  type Prefs = Record<(typeof PREF_KEYS)[number], boolean> & { id?: string };
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [uid, setUid] = useState<string | null>(null);
  const [emailLog, setEmailLog] = useState<{ id: string; kind: string; subject: string; status: string; created_at: string }[]>([]);

  const loadNotif = async () => {
    const { data: u } = await supabase.auth.getUser();
    const userId = u.user?.id ?? null; setUid(userId);
    if (userId) {
      const p = await supabase.from('notification_preferences').select('*').eq('user_id', userId).is('deleted_at', null).limit(1).maybeSingle();
      setPrefs((p.data as Prefs) ?? { email_enabled: true, alert_due_j30: true, alert_due_j7: true, alert_overdue: true, alert_wo_overdue: true, alert_anomaly: true });
    }
    const l = await supabase.from('email_log').select('id,kind,subject,status,created_at').is('deleted_at', null).order('created_at', { ascending: false }).limit(20);
    setEmailLog((l.data as any[]) ?? []);
  };

  async function togglePref(key: (typeof PREF_KEYS)[number]) {
    if (!org || !uid || !prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    if (prefs.id) await supabase.from('notification_preferences').update({ [key]: next[key] }).eq('id', prefs.id);
    else {
      const r = await supabase.from('notification_preferences').insert({ organization_id: org.id, user_id: uid, ...Object.fromEntries(PREF_KEYS.map((k) => [k, next[k]])) } as any).select('id').single();
      if (r.data) setPrefs({ ...next, id: (r.data as any).id });
    }
  }

  async function saveWindow() {
    if (!org || !(Number(win) > 0)) return;
    setSavedWin(false);
    const { error } = await supabase.from('organizations').update({ preventive_window_days: Number(win) }).eq('id', org.id);
    if (!error) setSavedWin(true);
  }

  const field = (label: string, value: string) => (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );

  return (
    <Layout header={<Topbar />}>
      <div className="mx-auto flex w-full max-w-[860px] flex-col gap-5 px-7 py-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.organization')}</h1>
        <Card className="grid grid-cols-2 gap-x-6 gap-y-5 p-6 sm:grid-cols-3">
          {org && (
            <>
              {field(t('settings.name'), org.name)}
              {field(t('settings.country'), org.country ?? '—')}
              {field(t('settings.timezone'), org.timezone)}
              {field(t('settings.currency'), org.default_currency)}
              {field(t('settings.locale'), org.default_locale.toUpperCase())}
            </>
          )}
        </Card>

        <h2 className="eyebrow">{t('settings.preventive')}</h2>
        <Card className="flex flex-wrap items-end gap-4 p-6">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs text-muted">{t('settings.preventiveWindow')}</span>
            <input type="number" min="1" value={win} onChange={(e) => { setWin(e.target.value); setSavedWin(false); }}
              className="h-9 w-32 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-accent" />
          </label>
          <Button variant="accent" onClick={saveWindow}>{t('common.save')}</Button>
          {savedWin && <span className="text-sm text-st-done-fg">{t('common.save')} ✓</span>}
          <span className="basis-full text-xs text-muted">{t('settings.preventiveHint')}</span>
        </Card>

        <h2 className="eyebrow">{t('cost.labour')}</h2>
        <Card className="flex flex-col gap-2 p-5">
          {rates.map((r) => (
            <div key={r.id} className="flex items-center justify-between border-b border-divider pb-2 text-sm last:border-0">
              <span className="font-medium">{r.name}</span>
              <span className="font-mono text-muted">{formatCurrency(r.hourly_rate, r.currency, i18n.language)}</span>
            </div>
          ))}
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <input value={newRate.name} onChange={(e) => setNewRate({ ...newRate, name: e.target.value })} placeholder={t('form.name')} className="h-9 min-w-0 flex-1 rounded-md border border-border bg-card px-3 text-sm outline-none" />
            <input value={newRate.hourly_rate} onChange={(e) => setNewRate({ ...newRate, hourly_rate: e.target.value })} type="number" step="0.01" placeholder={t('cost.rate')} className="h-9 w-28 rounded-md border border-border bg-card px-3 text-sm outline-none" />
            <Button onClick={addRate}>+ {t('cost.add')}</Button>
          </div>
          <label className="mt-2 flex flex-wrap items-center gap-2 border-t border-divider pt-3 text-sm">
            <span className="text-muted">{t('settings.defaultLabourRate')}</span>
            <select value={org?.default_labour_rate_id ?? ''} onChange={(e) => setDefaultRate(e.target.value)} className="h-9 rounded-md border border-border bg-card px-2.5 text-sm">
              <option value="">{t('common.none')}</option>
              {rates.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
        </Card>

        <h2 className="eyebrow">{t('crm.pipeline')}</h2>
        <Card className="flex flex-col gap-2 p-5">
          {stages.map((s) => (
            <div key={s.id} className="flex items-center justify-between border-b border-divider pb-2 text-sm last:border-0">
              <span className="flex items-center gap-2">
                <span className="font-mono text-xs text-muted">{s.position}</span>
                <span className="font-medium">{s.name_i18n?.[i18n.language] || s.name}</span>
                {s.is_won && <span className="rounded-pill bg-st-done-bg px-1.5 py-0.5 text-2xs text-st-done-fg">{t('crm.won')}</span>}
                {s.is_lost && <span className="rounded-pill bg-st-overdue-bg px-1.5 py-0.5 text-2xs text-st-overdue-fg">{t('crm.lost')}</span>}
              </span>
            </div>
          ))}
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <input value={newStage} onChange={(e) => setNewStage(e.target.value)} placeholder={t('crm.stageName')} className="h-9 min-w-0 flex-1 rounded-md border border-border bg-card px-3 text-sm outline-none" />
            <Button onClick={addStage}>+ {t('cost.add')}</Button>
          </div>
        </Card>

        <h2 className="eyebrow">{t('crm.leadSources')}</h2>
        <Card className="flex flex-col gap-2 p-5">
          <div className="flex flex-wrap gap-2">
            {sources.map((s) => <span key={s.id} className="rounded-pill border border-border bg-bg px-2.5 py-1 text-sm">{s.name_i18n?.[i18n.language] || s.name}</span>)}
          </div>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <input value={newSource} onChange={(e) => setNewSource(e.target.value)} placeholder={t('crm.sourceName')} className="h-9 min-w-0 flex-1 rounded-md border border-border bg-card px-3 text-sm outline-none" />
            <Button onClick={addSource}>+ {t('cost.add')}</Button>
          </div>
        </Card>

        <h2 className="eyebrow">{t('inv.taxes')}</h2>
        <Card className="flex flex-col gap-2 p-5">
          {taxes.map((x) => (
            <div key={x.id} className="flex items-center justify-between border-b border-divider pb-2 text-sm last:border-0">
              <span className="font-medium">{x.name}</span>
              <span className="font-mono text-muted">{x.rate_pct}% · {x.is_inclusive ? t('inv.inclusive') : t('inv.exclusive')}</span>
            </div>
          ))}
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <input value={newTax.name} onChange={(e) => setNewTax({ ...newTax, name: e.target.value })} placeholder={t('inv.taxLabel')} className="h-9 min-w-0 flex-1 rounded-md border border-border bg-card px-3 text-sm outline-none" />
            <input value={newTax.rate_pct} onChange={(e) => setNewTax({ ...newTax, rate_pct: e.target.value })} type="number" step="0.001" min="0" placeholder="%" className="h-9 w-20 rounded-md border border-border bg-card px-3 text-sm outline-none" />
            <select value={newTax.is_inclusive} onChange={(e) => setNewTax({ ...newTax, is_inclusive: e.target.value })} className="h-9 rounded-md border border-border bg-card px-2 text-sm outline-none"><option value="0">{t('inv.exclusive')}</option><option value="1">{t('inv.inclusive')}</option></select>
            <Button onClick={addTax}>+ {t('cost.add')}</Button>
          </div>
        </Card>

        <h2 className="eyebrow">{t('notif.title')}</h2>
        <Card className="flex flex-col gap-3 p-5">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {prefs && PREF_KEYS.map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={prefs[k]} onChange={() => togglePref(k)} disabled={k !== 'email_enabled' && !prefs.email_enabled} />
                {t('notif.' + k)}
              </label>
            ))}
          </div>
        </Card>
        {emailLog.length > 0 && (
          <Card className="overflow-hidden p-0">
            <table className="w-full border-collapse text-sm">
              <tbody>
                {emailLog.map((e) => (
                  <tr key={e.id} className="border-t border-divider first:border-0">
                    <td className="px-5 py-2.5">{e.subject}</td>
                    <td className="px-2 py-2.5 text-muted">{t('notif.' + e.kind, e.kind)}</td>
                    <td className="px-2 py-2.5 font-mono text-xs text-muted">{e.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        <h2 className="eyebrow">{t('settings.kpiTargets')}</h2>
        <Card className="overflow-hidden p-0">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {kpis.map((k) => (
                <tr key={k.kpi_key} className="border-t border-divider first:border-0">
                  <td className="px-5 py-3.5">{t('kpiKey.' + k.kpi_key, k.kpi_key)}</td>
                  <td className="px-5 py-3.5 text-right font-mono">{k.target_pct}%</td>
                </tr>
              ))}
              {kpis.length === 0 && <tr><td className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
    </Layout>
  );
}
